/* eslint-disable rxjs-x/no-ignored-subscription */
// We don't need unsubscribe observables in this service, because of using takeUntilDestroyed
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TenantService, UserTenantInformation } from '@api';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, InteractionStatus, Logger, LogLevel, PopupRequest, PromptValue, RedirectRequest } from '@azure/msal-browser';
import { Store } from '@ngxs/store';
import { BehaviorSubject, catchError, delay, filter, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoggingService } from '../shared/services/logging.service';
import { PushNotificationsService } from '../shared/services/push-notifications.service';
import { SetTenantUser, UserLoggedIn } from '../shared/state/application.actions';
import { IdTokenClaimsWithPolicyId } from './Id-token-claims-with-policy-id';
import { TENANT_ID } from './authorization-factories';

@Injectable()
export class AuthorizationService {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private tenantId: string = inject(TENANT_ID);
    private msalGuardConfig: MsalGuardConfiguration = inject(MSAL_GUARD_CONFIG) as unknown as MsalGuardConfiguration;
    private tenantService: TenantService = inject(TenantService);
    private store: Store = inject(Store);
    private authService: MsalService = inject(MsalService);
    private msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
    private logging: LoggingService = inject(LoggingService);
    private pushNotificationsService: PushNotificationsService = inject(PushNotificationsService);
    private router: Router = inject(Router);

    private isLoginInProgress$$ = new BehaviorSubject<boolean>(false);
    private hasPermissions$$ = new BehaviorSubject<boolean>(false);
    private hasAcceptedSecureEnvironmentDisclaimer$$ = new BehaviorSubject<boolean>(false);
    private passwordResetPending = false;

    public isLoginInProgress$ = this.isLoginInProgress$$.asObservable();
    public hasPermissions$ = this.hasPermissions$$.asObservable();
    public hasAcceptedSecureEnvironmentDisclaimer$ = this.hasAcceptedSecureEnvironmentDisclaimer$$.asObservable();

    constructor() {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                delay(0),
                tap(() => this.setMSALLogger())
            )
            .subscribe();
    }

    public subscribeLoginInProgress() {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    if (status === InteractionStatus.None) {
                        this.checkAndSetActiveAccount();
                        return;
                    }

                    this.logging.trace('Logging is in progress, waiting for msal to take over.');
                    this.isLoginInProgress$$.next(true);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public handleRedirectAfterLogin(eventMessage: EventMessage) {
        const payload = eventMessage.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (
            idtoken.acr?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.signUpSignIn.toLowerCase() ||
            idtoken.tfp?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.signUpSignIn.toLowerCase()
        ) {
            this.authService.instance.setActiveAccount(payload.account);
        }

        if (
            idtoken.acr?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.resetPassword.toLowerCase() ||
            idtoken.tfp?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.resetPassword.toLowerCase()
        ) {
            let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
                authority: environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.authorities.signUpSignIn.authority,
                scopes: [environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.connectPortalAPI, environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.openId],
                prompt: PromptValue.LOGIN,
            };

            this.login(signUpSignInFlowRequest);
        }
    }

    public handleRedirectPasswordReset(result: EventMessage) {
        // The user has forgotten their password. Need to initiate the password reset flow again.
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
            this.initiatePasswordReset();
        }
    }

    public handleAcquireToken() {
        const activeAccount = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()?.[0];
        const sessionId = activeAccount?.idTokenClaims?.['sid'];

        setTimeout(() => {
            this.authService
                .acquireTokenRedirect({
                    sid: sessionId,
                    scopes: [environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.connectPortalAPI, environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.openId],
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        });
    }

    private initiatePasswordReset() {
        if (this.passwordResetPending) {
            return;
        }

        this.passwordResetPending = true;
        const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.authorities.resetPassword.authority,
            scopes: [],
        };

        this.login(resetPasswordFlowRequest);
    }

    private setMSALLogger() {
        const loggerConfig = {
            loggerCallback: (logLevel: LogLevel, message: string, _containsPii: boolean) => {
                this.logMSAL(logLevel, message);
            },
            piiLoggingEnabled: false,
            logLevel: LogLevel.Verbose,
        };

        const logger = new Logger(loggerConfig) as unknown as ReturnType<typeof this.authService.getLogger>;
        this.authService.setLogger(logger);
    }

    private logMSAL(logLevel: LogLevel, message: string) {
        if (logLevel === LogLevel.Verbose) {
            this.logging.trace(message);
        } else if (logLevel === LogLevel.Info) {
            this.logging.info(message);
        } else if (logLevel === LogLevel.Warning) {
            this.logging.warn(message);
        } else if (logLevel === LogLevel.Error) {
            this.logging.error(message);
        }
    }

    private checkAndSetActiveAccount() {
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            let accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }

        if (!activeAccount) {
            if (!this.passwordResetPending) {
                this.login(undefined);
            }

            return;
        }

        this.store.dispatch(new UserLoggedIn(activeAccount));

        this.tenantService
            .getTenantUser(this.tenantId ?? '', 'response')
            .pipe(
                tap((tenantUserResult) => {
                    this.tenantPermissionsSucceeded(tenantUserResult.body);
                    this.startPushNotificationService();
                }),
                catchError((error) => {
                    this.tenantPermissionsFailed(error);
                    return throwError(() => error);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.isLoginInProgress$$.next(false);
    }

    private startPushNotificationService() {
        if (this.tenantId != null) {
            this.pushNotificationsService.start();
        }
    }

    private login(userFlowRequest?: RedirectRequest | PopupRequest) {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
        } else {
            this.authService.loginRedirect(userFlowRequest);
        }
    }

    private tenantPermissionsSucceeded(userTenantInformation: UserTenantInformation) {
        this.store.dispatch(new SetTenantUser(userTenantInformation));
        const hasAcceptedSecureEnvironmentDisclaimer = userTenantInformation.user.hasAcceptedSecureEnvironmentDisclaimer != null;

        this.hasPermissions$$.next(true);
        this.hasAcceptedSecureEnvironmentDisclaimer$$.next(hasAcceptedSecureEnvironmentDisclaimer);

        if (!this.hasAcceptedSecureEnvironmentDisclaimer$$.getValue() && window.location.href.indexOf('disclaimer') === -1) {
            this.logging.trace('Redirecting user to disclaimer page');

            this.router.navigate(['disclaimer']);
        }
    }

    private tenantPermissionsFailed(request: HttpErrorResponse) {
        if (this.isRedirectLoop(request)) {
            return;
        }

        if ([401, 403, 404].includes(request.status)) {
            this.handleUnauthorizedAccess(request);
        } else if (request.status === 302) {
            this.handleRedirect(request);
        }
    }

    private isRedirectLoop(request: HttpErrorResponse): boolean {
        return window.location.href.includes('not-found') || (request.status === 302 && this.tenantId == null && request.error.redirectUrl.includes('not-found'));
    }

    private handleUnauthorizedAccess(request: HttpErrorResponse) {
        if (this.tenantId == null) {
            return;
        }

        this.logging.error(`User not authorized (${request.status}) for tenant ${this.tenantId}, redirecting to not found page`, {
            tenantId: this.tenantId,
            redirectTo: '/not-found',
            status: request.status.toString(),
        });

        this.hasPermissions$$.next(false);
        window.location.href = '/not-found';
    }

    private handleRedirect(request: HttpErrorResponse) {
        const logMethod = window.location.href.indexOf('not-found') >= 0 ? this.logging.warn : this.logging.info;

        logMethod.bind(this.logging, `Redirecting to ${request.error.redirectUrl} because backend returned redirect response`, {
            tenantId: this.tenantId,
            redirectTo: request.error.redirectUrl,
            status: request.status.toString(),
        });

        window.location.href = request.error.redirectUrl;
    }
}
